import {
  type Branch,
  type CreateBranchInput,
  type CreateTerminalInput,
  createBranchInput,
  createTerminalInput,
  type Terminal,
} from '@facture/contracts'
import { zodResolver } from '@hookform/resolvers/zod'
import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import {
  GitBranchIcon,
  MapPinIcon,
  MonitorIcon,
  MoreHorizontalIcon,
  PencilIcon,
  PhoneIcon,
  PlusIcon,
  PowerIcon,
} from 'lucide-react'
import { m } from 'motion/react'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { RISE, STAGGER, TRANSITION } from '@/components/motion'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Field } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { EmptyState } from '@/components/ui/page'
import { Skeleton } from '@/components/ui/skeleton'
import { SequencesCard } from '@/features/settings/sequences-card'
import { api } from '@/lib/api'
import { useCan } from '@/lib/session'
import { cn } from '@/lib/utils'

export const Route = createFileRoute('/_app/settings/branches')({
  staticData: { crumb: 'Sucursales' },
  component: BranchesSettings,
})

const branchesQuery = queryOptions({ queryKey: ['branches'], queryFn: () => api.get<Branch[]>('/branches') })

type BranchDialog = { mode: 'create' } | { mode: 'edit'; branch: Branch } | null
type TerminalDialog = { mode: 'create'; branch: Branch } | { mode: 'edit'; terminal: Terminal } | null

function BranchesSettings() {
  const { data, isLoading } = useQuery(branchesQuery)
  const canManage = useCan('settings:manage')
  const queryClient = useQueryClient()
  const [branchDialog, setBranchDialog] = useState<BranchDialog>(null)
  const [terminalDialog, setTerminalDialog] = useState<TerminalDialog>(null)

  const toggleBranch = useMutation({
    mutationFn: (b: Branch) => api.patch(`/branches/${b.id}`, { isActive: !b.isActive }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['branches'] }),
  })
  const toggleTerminal = useMutation({
    mutationFn: (t: Terminal) => api.patch(`/terminals/${t.id}`, { isActive: !t.isActive }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['branches'] }),
  })

  return (
    <div className="grid gap-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-semibold text-lg tracking-tight">Sucursales y terminales</h2>
          <p className="text-muted-foreground text-sm">
            Forman parte de la clave y el consecutivo de cada comprobante (sucursal de 3 dígitos, terminal de
            5).
          </p>
        </div>
        {canManage && (
          <Button onClick={() => setBranchDialog({ mode: 'create' })}>
            <PlusIcon /> Nueva sucursal
          </Button>
        )}
      </div>

      {isLoading && (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <Skeleton className="h-48 rounded-xl" />
          <Skeleton className="h-48 rounded-xl" />
        </div>
      )}

      {data && data.length === 0 && (
        <Card>
          <EmptyState
            icon={GitBranchIcon}
            title="Sin sucursales"
            description="Creá tu casa matriz para empezar a emitir comprobantes."
          />
        </Card>
      )}

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        {data?.map((branch, index) => (
          <m.div
            key={branch.id}
            initial={{ opacity: 0, y: RISE }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ ...TRANSITION.enter, delay: Math.min(index, 8) * STAGGER }}
          >
            <Card className={cn('flex h-full flex-col', !branch.isActive && 'opacity-60')}>
              <div className="flex items-start gap-4 p-5">
                <div className="flex size-11 shrink-0 flex-col items-center justify-center rounded-xl bg-primary-soft text-primary-soft-foreground">
                  <span className="font-mono font-semibold text-sm leading-none">{branch.code}</span>
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <h3 className="truncate font-semibold">{branch.name}</h3>
                    {!branch.isActive && <Badge>Inactiva</Badge>}
                  </div>
                  <div className="mt-1.5 grid gap-1 text-muted-foreground text-xs">
                    {branch.address && (
                      <span className="flex items-center gap-1.5">
                        <MapPinIcon className="size-3.5" /> {branch.address}
                      </span>
                    )}
                    {branch.phone && (
                      <span className="flex items-center gap-1.5">
                        <PhoneIcon className="size-3.5" /> {branch.phone}
                      </span>
                    )}
                  </div>
                </div>
                {canManage && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon-sm" aria-label="Acciones">
                        <MoreHorizontalIcon />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onSelect={() => setBranchDialog({ mode: 'edit', branch })}>
                        <PencilIcon /> Editar
                      </DropdownMenuItem>
                      <DropdownMenuItem onSelect={() => setTerminalDialog({ mode: 'create', branch })}>
                        <PlusIcon /> Agregar terminal
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        onSelect={() => toggleBranch.mutate(branch)}
                        destructive={branch.isActive}
                      >
                        <PowerIcon /> {branch.isActive ? 'Desactivar' : 'Activar'}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </div>
              <div className="mt-auto border-border border-t bg-subtle px-5 py-3">
                <div className="mb-2 flex items-center justify-between font-medium text-muted-foreground text-xs">
                  <span>Terminales · {branch.terminals.length}</span>
                  {canManage && (
                    <button
                      type="button"
                      onClick={() => setTerminalDialog({ mode: 'create', branch })}
                      className="text-primary hover:underline"
                    >
                      Agregar
                    </button>
                  )}
                </div>
                <div className="flex flex-wrap gap-2">
                  {branch.terminals.map((terminal) => (
                    <button
                      key={terminal.id}
                      type="button"
                      disabled={!canManage}
                      onClick={() => setTerminalDialog({ mode: 'edit', terminal })}
                      className={cn(
                        'flex items-center gap-2 rounded-lg border border-border bg-card px-2.5 py-1.5 text-xs transition enabled:hover:border-foreground/20',
                        !terminal.isActive && 'opacity-50',
                      )}
                    >
                      <MonitorIcon className="size-3.5 text-muted-foreground" />
                      <span className="font-mono">{terminal.code}</span>
                      <span className="text-muted-foreground">{terminal.name}</span>
                    </button>
                  ))}
                </div>
              </div>
            </Card>
          </m.div>
        ))}
      </div>

      {canManage && <SequencesCard />}

      <BranchFormDialog state={branchDialog} onClose={() => setBranchDialog(null)} />
      <TerminalFormDialog
        state={terminalDialog}
        onClose={() => setTerminalDialog(null)}
        onToggle={(t) => toggleTerminal.mutate(t)}
      />
    </div>
  )
}

function BranchFormDialog({ state, onClose }: { state: BranchDialog; onClose: () => void }) {
  const queryClient = useQueryClient()
  const editing = state?.mode === 'edit' ? state.branch : null
  const form = useForm<CreateBranchInput>({
    resolver: zodResolver(createBranchInput),
    values: editing
      ? { code: editing.code, name: editing.name, address: editing.address, phone: editing.phone }
      : { code: '', name: '', address: null, phone: null },
  })
  const save = useMutation({
    mutationFn: (values: CreateBranchInput) =>
      editing
        ? api.patch(`/branches/${editing.id}`, {
            name: values.name,
            address: values.address,
            phone: values.phone,
          })
        : api.post('/branches', values),
    onSuccess: () => {
      toast.success(editing ? 'Sucursal actualizada' : 'Sucursal creada')
      queryClient.invalidateQueries({ queryKey: ['branches'] })
      onClose()
    },
  })
  const { errors } = form.formState

  return (
    <Dialog open={!!state} onOpenChange={(open) => !open && onClose()}>
      <DialogContent size="sm">
        <form onSubmit={form.handleSubmit((v) => save.mutate(v))} noValidate>
          <DialogHeader>
            <DialogTitle>{editing ? 'Editar sucursal' : 'Nueva sucursal'}</DialogTitle>
            <DialogDescription>El código no se puede cambiar una vez creada.</DialogDescription>
          </DialogHeader>
          <DialogBody className="grid gap-4">
            <div className="grid grid-cols-[6rem_1fr] gap-3">
              <Field label="Código" error={errors.code} required>
                {(c) => (
                  <Input
                    {...c}
                    {...form.register('code')}
                    disabled={!!editing}
                    className="font-mono"
                    placeholder="002"
                  />
                )}
              </Field>
              <Field label="Nombre" error={errors.name} required>
                {(c) => <Input {...c} {...form.register('name')} placeholder="Sucursal Escazú" />}
              </Field>
            </div>
            <Field label="Dirección" error={errors.address}>
              {(c) => <Input {...c} {...form.register('address', { setValueAs: (v) => v || null })} />}
            </Field>
            <Field label="Teléfono" error={errors.phone}>
              {(c) => (
                <Input {...c} {...form.register('phone', { setValueAs: (v) => v || null })} inputMode="tel" />
              )}
            </Field>
          </DialogBody>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancelar
            </Button>
            <Button type="submit" loading={save.isPending}>
              {editing ? 'Guardar' : 'Crear sucursal'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

function TerminalFormDialog({
  state,
  onClose,
  onToggle,
}: {
  state: TerminalDialog
  onClose: () => void
  onToggle: (t: Terminal) => void
}) {
  const queryClient = useQueryClient()
  const editing = state?.mode === 'edit' ? state.terminal : null
  const form = useForm<CreateTerminalInput>({
    resolver: zodResolver(createTerminalInput),
    values: editing ? { code: editing.code, name: editing.name } : { code: '', name: '' },
  })
  const save = useMutation({
    mutationFn: (values: CreateTerminalInput) =>
      editing
        ? api.patch(`/terminals/${editing.id}`, { name: values.name })
        : api.post(`/branches/${state?.mode === 'create' ? state.branch.id : ''}/terminals`, values),
    onSuccess: () => {
      toast.success(editing ? 'Terminal actualizada' : 'Terminal creada')
      queryClient.invalidateQueries({ queryKey: ['branches'] })
      onClose()
    },
  })
  const { errors } = form.formState

  return (
    <Dialog open={!!state} onOpenChange={(open) => !open && onClose()}>
      <DialogContent size="sm">
        <form onSubmit={form.handleSubmit((v) => save.mutate(v))} noValidate>
          <DialogHeader>
            <DialogTitle>{editing ? 'Editar terminal' : 'Nueva terminal'}</DialogTitle>
            <DialogDescription>
              {state?.mode === 'create'
                ? `Sucursal ${state.branch.code} · ${state.branch.name}`
                : 'Punto de emisión'}
            </DialogDescription>
          </DialogHeader>
          <DialogBody className="grid grid-cols-[7rem_1fr] gap-3">
            <Field label="Código" error={errors.code} required>
              {(c) => (
                <Input
                  {...c}
                  {...form.register('code')}
                  disabled={!!editing}
                  className="font-mono"
                  placeholder="00002"
                />
              )}
            </Field>
            <Field label="Nombre" error={errors.name} required>
              {(c) => <Input {...c} {...form.register('name')} placeholder="Caja 2" />}
            </Field>
          </DialogBody>
          <DialogFooter className="sm:justify-between">
            {editing ? (
              <Button
                type="button"
                variant={editing.isActive ? 'destructive-soft' : 'soft'}
                onClick={() => {
                  onToggle(editing)
                  onClose()
                }}
              >
                <PowerIcon /> {editing.isActive ? 'Desactivar' : 'Activar'}
              </Button>
            ) : (
              <span />
            )}
            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={onClose}>
                Cancelar
              </Button>
              <Button type="submit" loading={save.isPending}>
                Guardar
              </Button>
            </div>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
